import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { setAutoFreeze } from 'immer';
import moment from 'moment';
import dayjs from 'dayjs';

import { uipApiInstance } from '../../services';
import { logout } from '../auth/authSlice';
import { FEATURE_FLAGS_RETRY_TIMES, FEATURE_NAMES, GOAL_PRIORITY_ORDER, TermTabType } from '../../common/constants';
import {
    TenantPalette,
    formatpalette,
    newPalettes,
    mergeWithDefaultPalette,
    getNewPalette,
    TenantType
} from '../../themes/newPalette';
import { toLowercaseKeys } from '../../common/helper';
import { getFirstNEntriesFromObj, sortObject } from '../../common/helper';
import { checkFFEnabled, getClientJourneyFlowType } from '../../utils';

setAutoFreeze(false);

export type InvestmentFundOptions = {
    label: string;
    value: string;
    disabled?: boolean;
    order: number;
    duration?: string;
};

export type FetchNotificationsPayload = {
    tenant: string;
    source: string;
};

export type NotificationPlacement = 'LOGIN_SUMMARY' | 'NOTIFICATION_CENTER' | (string & {});
export type NotificationSeverity = 'INFORMATIONAL' | 'WARNING' | 'CRITICAL' | (string & {});
export type NotificationStatus = 'OPEN' | 'CLOSED' | (string & {});

export type NotificationAttributes = {
    event_name: string;
    event_type: string;
    goalCategory?: string;
    idempotency_key: string;
    portfolioBundleName?: string;
    portfolio_id?: string;
    portfolio_ref_id?: string;
    portfolio_type?: string;
    scope?: string;
    tenant_id: string;
    trigger?: string;
    [key: string]: any;
};

export type Notification = {
    attributes: NotificationAttributes;
    client_id: string | null;
    created_at: string;
    da_advice_id: string | null;
    dismissed: boolean;
    event_id: string;
    expired_at: string;
    goal_id: string | null;
    mapper_id: number;
    message: string;
    notification_id: string;
    placement: NotificationPlacement[];
    portfolio_id: string | null;
    priority: number;
    proposal_id: string | null;
    read: boolean;
    severity: NotificationSeverity;
    status: NotificationStatus;
    title: string;
};

export type NotificationsResponse = {
    results: Record<string, Notification[]>;
    success: boolean;
};

// Shared unread predicate so Nav (bell dot) and the Notifications panel can't drift: a
// notification is unread when the tenant read-marker is unset or predates its created_at.
export const isNotificationUnread = (notification: Notification, latestTimestamp?: string): boolean =>
    !latestTimestamp || dayjs(latestTimestamp).isBefore(notification.created_at);

export type GoalCategoriesType = {
    [key: string]: {
        label: string;
        value: string;
        order: number;
        [key: string]: any;
    }[];
};

type MarketPortfoliosType = {
    tactical: any[];
    strategic: any[];
    shortTerm?: {
        tactical: any[];
        strategic: any[];
        bundleType?: string;
    };
    mediumTerm?: {
        tactical: any[];
        strategic: any[];
        bundleType?: string;
    };
    longTerm?: {
        tactical: any[];
        strategic: any[];
        bundleType?: string;
    };
    showModal?: boolean;
    showBanner?: boolean;
    showPdfViewer?: boolean;
};

type InvestmentAccountType = {
    [key: string]: InvestmentFundOptions[];
};
type InvestmentLocationType = {
    [key: string]: InvestmentFundOptions[];
};

type CMStype = {
    [key: string]: string;
};

type MinMaxEquityType = {
    Aggressive: {
        maxEquity: number;
        minEquity: number;
        riskProfile: string;
    };
    Conservative: {
        maxEquity: number;
        minEquity: number;
        riskProfile: string;
    };
    Moderate: {
        maxEquity: number;
        minEquity: number;
        riskProfile: string;
    };
};

type GoalPriorityProbabilitiesType =
    | { desire: number; need: number; dream: number; wish: number; want: number }
    | { Dream: number; Need: number; Wish: number; Want: number };

export type GoalConstraint = {
    goalType: string;
    duration?: string;
    preRetirementConstraints?: unknown;
    postRetirementConstraints?: unknown;
    [key: string]: any;
};

export type UserLevelConfig = {
    goalConstraints?: GoalConstraint[];
    goalPriorityProbabilities?: GoalPriorityProbabilitiesType;
    goalPriorityLevelsToConsider?: number;
    reallocationSchedule?: {
        frequency?: 'half-yearly' | 'quarterly' | 'yearly';
        datesByFrequency?: Record<string, string[]>;
    };
    [key: string]: any;
};

export type GoeConfigType = {
    minMaxEquity?: MinMaxEquityType;
    goalPriorityProbabilities?: GoalPriorityProbabilitiesType;
    realisticGoalProb?: number;
    reallocSchedules?: string[];
    scheduleFrequency?: string;
    riskProfiles?: string[];
    portfolioLevelConfig?: any;
    userLevelConfig?: UserLevelConfig;
    [key: string]: any;
};

type InitialState = {
    loading: boolean;
    globalLoader: boolean;
    loaderMessage: string | null;
    error?: null | undefined | any;
    showErrorModal?: boolean;
    globalConfig: {
        tenantType: string;
        termsOfUse: boolean;
        eula: boolean;
        language: string[];
        logo?: string;
        subdomain: string;
        currencySymbol: string;
        currencyLocale: string;
        dateFormat?: string;
        disclaimers: { brandName: string };
        themePalette?: TenantPalette;
        /** True when the tenant maps goals to multiple (term based) portfolios, i.e. Banorte. */
        isMultiPortfolioTenant?: boolean;
        [key: string]: any;
    };
    multiTenancyRefresh?: any;
    orgInfo?: {
        investmentAccountType?: any;
        investmentAccountLocation?: InvestmentLocationType;
        investmentContributionPercentage?: InvestmentAccountType;
        riskProfileAex?: any;
        incomeSourceType?: any;
        relationshipTypes?: any;
        orgEmail?: string;
        userAgreement?: any;
        hantz?: any;
        eula?: any;
        tenantId?: string;
        currencies?: any;
        goalCategories?: GoalCategoriesType;
    };
    showAgreementModal: boolean;
    showDisclaimerFooter: boolean;
    portfolio: Array<any>;
    cmsData?: CMStype;
    marketPortfolios: MarketPortfoliosType;
    domainEmail?: string;
    goeConfig: GoeConfigType;
    featureFlags?: Array<string>;
    isCookiePolicyAlertBoxClosed?: boolean;
    openDrawer?: string;
    uiAppState?: {
        [key: string]: any;
    };
    // Funding Navigator selected term tab (Short/Medium/Long). Kept in redux so streamed EWS
    // updates never reset it and the step can stay decoupled from the adviceGoals slice.
    selectedFundingTermIndex?: TermTabType;
    currentLanguage?: string;
    notifications: Notification[];
    apiErrors?: {
        getTenantBasedConfig?: boolean;
        getOrganizationConfig?: boolean;
        fetchCMSdata?: boolean;
        fetchPortfolio?: boolean;
        fetchGoeConfig?: boolean;
    };
};

const initialState: InitialState = {
    loading: false,
    globalLoader: false,
    loaderMessage: null,
    error: {},
    showErrorModal: false,
    multiTenancyRefresh: '',
    orgInfo: { orgEmail: '' },
    showAgreementModal: false,
    showDisclaimerFooter: true,
    portfolio: [],
    marketPortfolios: {
        strategic: [],
        tactical: [],
        showModal: false,
        showBanner: false,
        showPdfViewer: false
    },
    domainEmail: '',
    goeConfig: {
        pipe_config: {
            goal_priority_prob_list: {
                Dream: 0.5,
                Need: 0.85,
                Want: 0.7,
                Wish: 0.6,
                Aspire: 0.6,
                Desire: 0.7
            },
            realloc_schedules: ['01-01-2025']
        }
    },
    featureFlags: undefined,
    isCookiePolicyAlertBoxClosed: undefined,
    currentLanguage: localStorage.getItem('userLanguage') || 'en', // Read from localStorage
    globalConfig: {
        tenantType: 'Trial',
        language: ['en'],
        currencySymbol: 'USD',
        currencyLocale: 'en-US',
        dateFormat: 'MM/DD/YYYY',
        termsOfUse: false,
        eula: false,
        logo: undefined,
        subdomain: '',
        disclaimers: { brandName: '' },
        themePalette: formatpalette(newPalettes['advice']),
        isMultiPortfolioTenant: false
    },
    openDrawer: '',
    uiAppState: {},
    notifications: [],
    selectedFundingTermIndex: TermTabType.SHORT,
    apiErrors: {
        getTenantBasedConfig: false,
        getOrganizationConfig: false,
        fetchCMSdata: false,
        fetchPortfolio: false,
        fetchGoeConfig: false
    }
};

const makeUseMemoRun = () => `#${Math.floor(Math.random() * 16777215).toString(16)}`;

export const logEventToBackEnd = createAsyncThunk(
    'globalState/logEventToBackEnd',
    async (action: string, { getState }) => {
        const state: any = getState();
        // Get tenant from auth state or fall back to subdomain from global state
        const tenant = state?.auth?.user?.tenant || state?.global?.globalConfig?.subdomain || 'advice';
        const response = await uipApiInstance({
            method: 'POST',
            url: '/api/advice/actionsaudit',
            data: {
                action,
                tenant: tenant
            }
        });

        return response;
    }
);

export const updateLanguage = createAsyncThunk(
    'globalState/updateLanguage',
    async (
        { tenantId, language, i18n }: { tenantId: string; language: string; i18n: any },
        { rejectWithValue, dispatch }
    ) => {
        try {
            i18n.changeLanguage(language);
            /** Fetch CMS data if tenantId is available. This will avoid failed calls on login page */
            if (tenantId) {
                await dispatch(fetchCMSdata({ language, tenantId: tenantId })); // Fetch CMS data
            }
            return language;
        } catch (err) {
            console.log(err);
            return rejectWithValue(err);
        }
    }
);

export const getTenantBasedConfig = createAsyncThunk(
    'globalState/getTenantBasedConfig',
    async (
        {
            tenantId,
            i18n,
            isLoader = false,
            callCMS = true
        }: { tenantId: string; i18n: any; isLoader: boolean; callCMS?: boolean },
        { rejectWithValue, dispatch }
    ) => {
        try {
            const response = await uipApiInstance({
                method: 'GET',
                url: `/api/advice/gettenantinfodns?subdomain=${tenantId}`,
                withCredentials: false
            });
            const tenantConfigs = response.data.tenantConfig?.sort((a: any, b: any) => {
                const convertToTime = (date: any) => moment(date).valueOf();

                return convertToTime(b?.__meta?.timestamp) - convertToTime(a?.__meta?.timestamp);
            });

            // Language is now user-specific (managed via localStorage), not tenant-specific
            // Use current i18n language or default to 'en'
            console.log(i18n.language, localStorage.getItem('userLanguage'), 'Determining language for tenant config');
            const currentLanguage = i18n.language || localStorage.getItem('userLanguage') || 'en';

            if (callCMS) {
                dispatch(fetchCMSdata({ language: currentLanguage, tenantId: tenantId })); // Fetch CMS data
            }

            return {
                ...tenantConfigs?.[0],
                themePalette: tenantConfigs?.[0]?.themePalette
                    ? formatpalette(mergeWithDefaultPalette(JSON.parse(tenantConfigs?.[0]?.themePalette), tenantId))
                    : formatpalette(getNewPalette(tenantId as TenantType)),
                currentLanguage,
                tenantType: tenantConfigs?.[0].tenantType || 'trial' || 'demo',
                isLoader,
                currencySymbol:
                    tenantConfigs?.[0].currencySymbol && tenantConfigs?.[0].currencySymbol !== ' '
                        ? tenantConfigs?.[0].currencySymbol
                        : 'en',
                dateFormat: tenantConfigs?.[0].dateFormat || 'MM/DD/YYYY',
                // AEX-3108: more than one goal-category portfolio mapping means term (Short/Medium/Long) based portfolios.
                isMultiPortfolioTenant: Object.keys(tenantConfigs?.[0]?.goalCategoryPortfolioMappings || {}).length > 1,
                // AEX-3363: optimistic-concurrency id for tenant info; source of truth for updatetenantinfo.
                tenant_info_ref_id: tenantConfigs?.[0]?.tenant_info_ref_id ?? response.data?.tenant_info_ref_id ?? null
            };
        } catch (err) {
            console.log(err);
            dispatch(logEventToBackEnd('GET_TENANT_INFO_API_ERROR'));
            return rejectWithValue(err);
        }
    }
);

export const getOrganizationConfig = createAsyncThunk(
    'globalState/getOrganizationConfig',
    async (tenant: string, { rejectWithValue, dispatch }) => {
        // const state: any = getState();

        // const tenantId = state?.global?.globalConfig?.subdomain;
        try {
            const response = await uipApiInstance({
                method: 'GET',
                url: `/api/advice/getorganizationconfig?tenant=${tenant}`,
                withCredentials: false
            });

            return response?.data?.tenantConfig?.[0];
        } catch (err) {
            console.log(err);
            dispatch(logEventToBackEnd('GET_ORGANIZATION_CONFIG_API_ERROR'));
            return rejectWithValue(err);
        }
    }
);

export const getFeatureFlags = createAsyncThunk('globalState/getFeatureFlags', async () => {
    const names = Object.values(FEATURE_NAMES);
    if (names.length) {
        let ffData: { featureflag_enabled: any; featureflag: string }[] = [];
        let retryAttempts = 0;
        while (ffData.length === 0 && retryAttempts < FEATURE_FLAGS_RETRY_TIMES) {
            try {
                const res = await uipApiInstance({
                    method: 'GET',
                    url: '/api/advice/featureflag',
                    params: { req: JSON.stringify(names) }
                });
                if (res?.status === 200) ffData = res.data?.data || res.data;
            } catch (error) {
                console.error('Failed to initialize feature flags:', error);
            }
            retryAttempts++;
        }
        const enabledFlags = new Set<string>();
        ffData?.forEach((flag: { featureflag_enabled: any; featureflag: string }) => {
            if (flag.featureflag_enabled) {
                enabledFlags.add(flag.featureflag);
            }
        });
        return Array.from(enabledFlags);
    }
});

export const updateTenantInfo = createAsyncThunk(
    'globalState/updateTenantInfo',
    async (requestedPayload: any, { dispatch, rejectWithValue, getState }) => {
        let url = `/api/advice/updatetenantinfo`;
        try {
            const state: any = getState();
            // AEX-3363: flow2 (banorte) only + FF — echo tenant_info_ref_id so the backend can reject a
            // tenant-info save made against a plan changed in another tab. Non-flow2 stays untouched.
            const isFlow2 = getClientJourneyFlowType(state?.auth?.user?.tenant) === 'flow2';
            const tenantInfoRefId = state?.global?.globalConfig?.tenant_info_ref_id;
            const payload =
                checkFFEnabled(FEATURE_NAMES['AEX-3342_ADD_VALIDATION_BEFORE_UPDATE']) && isFlow2 && tenantInfoRefId
                    ? { ...requestedPayload, tenant_info_ref_id: tenantInfoRefId }
                    : requestedPayload;
            const response = await uipApiInstance({
                method: 'POST',
                url,
                withCredentials: false,
                data: payload
            });

            return response?.data;
        } catch (err) {
            console.log(err);
            dispatch(logEventToBackEnd('UPDATE_TENANT_INFO_API_ERROR'));
            return rejectWithValue(err);
        }
    }
);

export const fetchPortfolio = createAsyncThunk(
    'globalState/fetchPortfolio',
    async (tenant: string, { rejectWithValue, getState, dispatch }) => {
        try {
            const state: any = getState();

            const isAuthenticated = state.auth.isAuthenticated;
            const orgEmail = state.global.orgInfo?.orgEmail;
            // const tenantId = state?.global?.globalConfig?.subdomain;
            const tenantId = tenant || state?.auth?.user?.tenant;

            if (!isAuthenticated) {
                return { portfolios: [], domainEmail: '' };
            }

            const response = await uipApiInstance({
                method: 'GET',
                url: `/api/advice/marketportfolios?tenant=${tenantId}`,
                withCredentials: false
            });

            const records = (response?.data?.data || []).filter((y: any) => y.orgEmail === orgEmail);
            const ts = (r: any) => moment(r?.__meta?.timestamp, 'YYYY-MM-DD HH:mm:ss.SSSS').valueOf();
            const latestOverall = records.length ? records.reduce((a: any, b: any) => (ts(a) > ts(b) ? a : b)) : null;

            const latestByType: Record<string, any | null> = { tactical: null, strategic: null };
            const latestByTermAndType: Record<
                'shortTerm' | 'mediumTerm' | 'longTerm',
                { tactical: any | null; strategic: any | null }
            > = {
                shortTerm: { tactical: null, strategic: null },
                mediumTerm: { tactical: null, strategic: null },
                longTerm: { tactical: null, strategic: null }
            };

            const getTermKeyFromGoalCategory = (
                goalCategory: 'Short Term' | 'Medium Term' | 'Long Term'
            ): 'shortTerm' | 'mediumTerm' | 'longTerm' | null => {
                if (goalCategory === 'Short Term') return 'shortTerm';
                if (goalCategory === 'Medium Term') return 'mediumTerm';
                if (goalCategory === 'Long Term') return 'longTerm';
                return null;
            };

            for (const rec of records) {
                const typeKey = String(rec?.portfolio_type || '').toLowerCase();
                if (typeKey === 'tactical' || typeKey === 'strategic') {
                    const current = latestByType[typeKey];
                    if (!current || ts(rec) > ts(current)) {
                        latestByType[typeKey] = rec;
                    }

                    const termKey = getTermKeyFromGoalCategory(rec?.goalCategory);
                    if (termKey) {
                        const currentByTerm = latestByTermAndType[termKey][typeKey];
                        if (!currentByTerm || ts(rec) > ts(currentByTerm)) {
                            latestByTermAndType[termKey][typeKey] = rec;
                        }
                    }
                }
            }

            const extractData = (rec: any) => rec?.portfolioData?.data || rec?.portfolioData?.config?.data || [];
            // TODO: FF if needed: DA_Flow2_FE-AEX-2751-AEX-2626
            // Create new marketPortfolios obj only for multiportfolio tenants (Banorte) to hold 3 types - short, med, long terms
            let marketPortfolios: any = {};
            if (state?.global?.globalConfig?.isMultiPortfolioTenant) {
                const getBundleType = (termKey: 'shortTerm' | 'mediumTerm' | 'longTerm') =>
                    latestByTermAndType[termKey].tactical?.portfolioBundleName ||
                    latestByTermAndType[termKey].strategic?.portfolioBundleName ||
                    '';

                marketPortfolios = {
                    /* tactical: extractData(latestByType.tactical),
                    strategic: extractData(latestByType.strategic), */
                    shortTerm: {
                        tactical: extractData(latestByTermAndType.shortTerm.tactical),
                        strategic: extractData(latestByTermAndType.shortTerm.strategic),
                        bundleType: getBundleType('shortTerm')
                    },
                    mediumTerm: {
                        tactical: extractData(latestByTermAndType.mediumTerm.tactical),
                        strategic: extractData(latestByTermAndType.mediumTerm.strategic),
                        bundleType: getBundleType('mediumTerm')
                    },
                    longTerm: {
                        tactical: extractData(latestByTermAndType.longTerm.tactical),
                        strategic: extractData(latestByTermAndType.longTerm.strategic),
                        bundleType: getBundleType('longTerm')
                    }
                };
            } else {
                marketPortfolios = {
                    tactical: extractData(latestByType.tactical),
                    strategic: extractData(latestByType.strategic)
                };
            }

            return {
                portfolios: extractData(latestOverall),
                domainEmail: latestOverall?.orgEmail || orgEmail || '',
                marketPortfolios
            };
        } catch (err) {
            dispatch(logEventToBackEnd(`MARKET_PORFOLIOS_API_ERROR: ${err}`));
            return rejectWithValue(err);
        }
    }
);

export const fetchGoeConfig = createAsyncThunk(
    'globalState/fetchGoeConfig',
    async (
        { tenant, clientemail }: { tenant: string; clientemail?: string },
        { rejectWithValue, getState, dispatch }
    ) => {
        try {
            const state: any = getState();
            const isAuthenticated = state.auth.isAuthenticated;
            const isNewGetGoeConfigAPIEnabled = state?.global?.featureFlags?.includes(
                'AEX-2821_new_getgoeconfig_endpoint_integration'
            );

            if (!isAuthenticated) {
                return {};
            }

            const response = await uipApiInstance({
                method: 'GET',
                url: `/api/advice/getgoeconfig?tenant=${tenant}`,
                withCredentials: false,
                headers: {
                    generatepayloadonly: true,
                    clientemail: clientemail || state?.global?.orgInfo?.orgEmail
                }
            });

            const responseData: any = response?.data?.body;
            const portfolioLevelConfig = Array.isArray(responseData?.portfolioLevelConfig)
                ? responseData?.portfolioLevelConfig
                : [responseData?.portfolioLevelConfig];
            const portfolioConfig = portfolioLevelConfig[0];
            const rawScheduleFrequency = responseData?.userLevelConfig?.reallocationSchedule?.frequency as
                | 'half-yearly'
                | 'quarterly'
                | 'yearly'
                | undefined;
            // Normalize to the dash-less key ('half-yearly' -> 'halfyearly') used by both
            // datesByFrequency and REALLOC_FREQUENCY_COLUMN_COUNT so consumers stay consistent.
            const scheduleFrequency = rawScheduleFrequency?.replace('-', '');
            const goalPriorityProbabilities = (toLowercaseKeys(
                responseData?.userLevelConfig?.goalPriorityProbabilities as Record<string, number> | undefined
            ) || {}) as Record<string, number>;
            const goalPriorityLevelsToConsider =
                responseData?.userLevelConfig?.goalPriorityLevelsToConsider ||
                Object.entries(goalPriorityProbabilities).length;
            // TODO: Remove pipe_config empty check, when FF AEX-2821_new_getgoeconfig_endpoint_integration is cleanedup.
            const normalizedResponseData =
                isNewGetGoeConfigAPIEnabled && !responseData?.pipe_config
                    ? {
                          minMaxEquity: portfolioConfig?.minMaxEquity,
                          goalPriorityProbabilities: getFirstNEntriesFromObj(
                              sortObject(goalPriorityProbabilities, GOAL_PRIORITY_ORDER),
                              goalPriorityLevelsToConsider
                          ),
                          realisticGoalProb: portfolioConfig?.realistic_goal_prob,
                          reallocSchedules: scheduleFrequency
                              ? responseData?.userLevelConfig?.reallocationSchedule?.datesByFrequency?.[
                                    scheduleFrequency.replace('-', '')
                                ]
                              : undefined,
                          scheduleFrequency,
                          riskProfiles: portfolioConfig?.riskProfiles,
                          portfolioLevelConfig: portfolioLevelConfig,
                          userLevelConfig: responseData?.userLevelConfig
                      }
                    : {
                          minMaxEquity: {},
                          goalPriorityProbabilities: toLowercaseKeys(
                              responseData?.pipe_config?.goal_priority_prob_list
                          ),
                          realisticGoalProb: responseData?.pipe_config?.realistic_goal_prob,
                          reallocSchedules: responseData?.pipe_config?.realloc_schedules,
                          scheduleFrequency: undefined,
                          riskProfiles: [],
                          portfolioLevelConfig: [],
                          userLevelConfig: responseData?.userLevelConfig
                      };

            return normalizedResponseData;
        } catch (err) {
            console.log(err);
            dispatch(logEventToBackEnd('GET_GOE_CONFIG_API_ERROR'));
            return rejectWithValue(err);
        }
    }
);

export const saveUserAgreementsSettings = createAsyncThunk(
    'globalState/saveUserAgreementsSettings',
    async (requestedPayload: any, { dispatch, rejectWithValue }) => {
        try {
            const response = await uipApiInstance({
                method: 'POST',
                url: '/api/advice/update_user_settings',
                withCredentials: false,
                data: requestedPayload
            });
            return { response: response?.data };
        } catch (err) {
            console.log(err);
            dispatch(logEventToBackEnd('UPDATE_USER_AGREEMENT_SETTINGS_ERROR'));
            return rejectWithValue(err);
        }
    }
);

export const fetchCMSdata = createAsyncThunk(
    'globalState/fetchCMSdata',
    async ({ tenantId, language }: { tenantId: string; language: string }, { rejectWithValue, dispatch }) => {
        try {
            const response = await uipApiInstance({
                method: 'GET',
                url: `/api/advice/getCMS?tenant=${tenantId}&language=${language}`,
                withCredentials: false
            });
            return {
                cmsData: response?.data?.cms_data || []
            };
        } catch (err) {
            dispatch(logEventToBackEnd(`CMS_API_ERROR: ${err}`));
            return rejectWithValue(err);
        }
    }
);

export const fetchNotifications = createAsyncThunk(
    'globalState/fetchNotifications',
    async ({ tenant, source }: FetchNotificationsPayload, { rejectWithValue, dispatch }) => {
        try {
            const response = await uipApiInstance({
                method: 'POST',
                url: '/api/advice/notifications',
                withCredentials: false,
                data: { tenant, source }
            });

            const results = (response?.data as NotificationsResponse)?.results;
            return (results?.[tenant] ?? []) as Notification[];
        } catch (err) {
            dispatch(logEventToBackEnd(`GET_NOTIFICATIONS_API_ERROR: ${err}`));
            return rejectWithValue(err);
        }
    }
);

// Persists the notification read-marker when the advisor opens the bell. Kept separate from
// saveUserAgreementsSettings so this non-blocking action never touches shared
// loading/error/showAgreementModal state (it has no extraReducers); failures are logged only.
export const updateNotificationReadTimestamp = createAsyncThunk(
    'globalState/updateNotificationReadTimestamp',
    async (
        requestedPayload: { from_tenant: string; latestNotificationTimestamp: string },
        { dispatch, rejectWithValue }
    ) => {
        try {
            const response = await uipApiInstance({
                method: 'POST',
                url: '/api/advice/update_user_settings',
                withCredentials: false,
                data: requestedPayload
            });
            return { response: response?.data };
        } catch (err) {
            dispatch(logEventToBackEnd(`UPDATE_NOTIFICATION_READ_TIMESTAMP_ERROR: ${err}`));
            return rejectWithValue(err);
        }
    }
);

const globalSlice = createSlice({
    name: 'globalState',
    initialState,
    reducers: {
        updateFactsheetModalVisibility: (
            state,
            action: PayloadAction<{ showModal: boolean; showBanner: boolean; showPdfViewer: boolean }>
        ) => {
            state.marketPortfolios.showModal = action.payload.showModal;
            state.marketPortfolios.showBanner = action.payload.showBanner;
            state.marketPortfolios.showPdfViewer = action.payload.showPdfViewer;
        },
        updateErrorModalState: (state, action: PayloadAction<any>) => {
            state.showErrorModal = action.payload.showErrorModal;
            state.error = action.payload.error;
        },
        updateAdminConfig: (state, action: PayloadAction<any>) => {
            state.multiTenancyRefresh = makeUseMemoRun();
            state.globalConfig = {
                ...state.globalConfig,
                styles: {
                    ...state.globalConfig.styles,
                    color: action.payload.color
                },
                tenantType: action.payload.tenantType,
                currencySymbol: action.payload.currencySymbol,
                currencyLocale: action.payload.currencyLocale,
                termsOfUse: action.payload.termsOfUse,
                eula: action.payload.eula
            };
        },
        updateLogo: (state, action: PayloadAction<any>) => {
            state.globalConfig.logo = action.payload;
        },
        updateSubdomain: (state, action: PayloadAction<any>) => {
            state.globalConfig.subdomain = action.payload;
        },
        setOpenDrawer: (state, action: PayloadAction<string>) => {
            state.openDrawer = action.payload;
        },
        updateGlobalLoaderState: (state, action: PayloadAction<any>) => {
            if (typeof action.payload === 'boolean') {
                if (action.payload) {
                    state.globalLoader = true;
                } else {
                    state.globalLoader = false;
                    state.loaderMessage = null;
                }
            } else if (typeof action.payload === 'object') {
                state.globalLoader = action.payload.enabled;
                state.loaderMessage = action.payload.message;
            }
        },
        handleOpenAgreementModal: (state, action: PayloadAction<any>) => {
            state.showAgreementModal = action.payload;
        },
        updateShowDisclaimerFooter: (state, action: PayloadAction<any>) => {
            state.showDisclaimerFooter = action.payload;
        },
        resetGlobalReducer: () => ({
            ...initialState,
            apiErrors: {
                getTenantBasedConfig: false,
                getOrganizationConfig: false,
                fetchCMSdata: false,
                fetchPortfolio: false,
                fetchGoeConfig: false
            }
        }),
        updateCookiePolicyAlertBoxClosed: (state, action: PayloadAction<any>) => {
            state.isCookiePolicyAlertBoxClosed = action.payload;
        },
        updatePlanState: (state, action: PayloadAction<{ key: string; value: any }>) => {
            const { key, value } = action.payload;
            state.uiAppState = {
                ...state.uiAppState,
                [key]: value
            };
        },
        initializePlanState: (state, action: PayloadAction<any>) => {
            state.uiAppState = action.payload;
        },
        setSelectedFundingTermIndex: (state, action: PayloadAction<TermTabType>) => {
            state.selectedFundingTermIndex = action.payload;
        }
    },
    extraReducers: (builder) => {
        builder.addCase(logout, (state) => ({
            ...initialState,
            globalConfig: state.globalConfig,
            apiErrors: {
                getTenantBasedConfig: false,
                getOrganizationConfig: false,
                fetchCMSdata: false,
                fetchPortfolio: false,
                fetchGoeConfig: false
            }
        })),
            builder.addCase(fetchPortfolio.fulfilled, (state, action) => {
                state.loading = false;
                state.portfolio = action.payload.portfolios;
                state.domainEmail = action.payload.domainEmail;
                if (action.payload.marketPortfolios) {
                    state.marketPortfolios = {
                        ...state.marketPortfolios,
                        ...action.payload.marketPortfolios
                    };
                }
                state.error = {};
                state.showErrorModal = false;
                if (state.apiErrors) state.apiErrors.fetchPortfolio = false;
            }),
            builder.addCase(fetchPortfolio.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error;
                if (state.apiErrors) state.apiErrors.fetchPortfolio = true;
            }),
            builder.addCase(fetchGoeConfig.fulfilled, (state, action) => {
                state.loading = false;
                state.goeConfig = action.payload;
                state.error = {};
                state.showErrorModal = false;
                if (state.apiErrors) state.apiErrors.fetchGoeConfig = false;
            }),
            builder.addCase(fetchGoeConfig.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error;
                if (state.apiErrors) state.apiErrors.fetchGoeConfig = true;
            }),
            // builder.addCase(getTenantBasedConfig.pending, (state) => {
            //     // commented out to avoid showing loader on loading root login page
            //     state.globalLoader = true;
            // }),
            builder.addCase(getTenantBasedConfig.fulfilled, (state, action) => {
                // state.globalLoader = false;
                if (action.payload.isLoader) {
                    state.globalLoader = false;
                }
                state.globalConfig = action.payload;
                state.error = {};
                state.showErrorModal = false;
                if (state.apiErrors) state.apiErrors.getTenantBasedConfig = false;
            }),
            builder.addCase(getTenantBasedConfig.rejected, (state, action) => {
                state.globalLoader = false;
                state.error = action.error;
                if (state.apiErrors) state.apiErrors.getTenantBasedConfig = true;
                const failedTenantId = action.meta?.arg?.tenantId;
                if (failedTenantId) {
                    state.globalConfig.themePalette = formatpalette(getNewPalette(failedTenantId as TenantType));
                }
            }),
            builder.addCase(updateTenantInfo.fulfilled, (state, action) => {
                state.globalConfig = {
                    ...state.globalConfig,
                    // AEX-3363: keep the freshest tenant_info_ref_id so the next save isn't seen as stale.
                    ...(action.payload?.tenant_info_ref_id
                        ? { tenant_info_ref_id: action.payload.tenant_info_ref_id }
                        : {}),
                    logo: action.payload?.logo,
                    secondaryLogo: action.payload?.secondaryLogo,
                    themePalette: action.payload?.themePalette
                        ? formatpalette(
                              mergeWithDefaultPalette(
                                  JSON.parse(action.payload?.themePalette),
                                  state.globalConfig.subdomain
                              )
                          )
                        : state.globalConfig.themePalette,
                    dateFormat: action.payload?.dateFormat || state.globalConfig.dateFormat,
                    currencySymbol: action.payload?.currencySymbol || state.globalConfig.currencySymbol,
                    currencyLocale: action.payload?.currencyLocale || state.globalConfig.currencyLocale,
                    tenantType: action.payload?.tenantType || state.globalConfig.tenantType
                };
            }),
            builder.addCase(getOrganizationConfig.fulfilled, (state, action) => {
                state.orgInfo = action.payload.orgInfo;
                if (state.apiErrors) state.apiErrors.getOrganizationConfig = false;
            }),
            builder.addCase(getOrganizationConfig.rejected, (state, action) => {
                state.error = action.error;
                if (state.apiErrors) state.apiErrors.getOrganizationConfig = true;
            }),
            builder.addCase(saveUserAgreementsSettings.pending, (state) => {
                state.loading = true;
            }),
            builder.addCase(saveUserAgreementsSettings.fulfilled, (state, action) => {
                state.loading = false;
                state.error = '';
                if (action?.payload?.response?.success) {
                    state.showAgreementModal = false;
                }
            }),
            builder.addCase(saveUserAgreementsSettings.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error;
            }),
            builder.addCase(getFeatureFlags.fulfilled, (state, action) => {
                state.featureFlags = action.payload;
            }),
            builder.addCase(fetchCMSdata.fulfilled, (state, action) => {
                state.loading = false;
                state.cmsData = action.payload.cmsData;
                state.error = {};
                state.showErrorModal = false;
                if (state.apiErrors) state.apiErrors.fetchCMSdata = false;
            }),
            builder.addCase(fetchCMSdata.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error;
                if (state.apiErrors) state.apiErrors.fetchCMSdata = true;
            }),
            builder.addCase(updateLanguage.pending, (state) => {
                state.globalLoader = true;
            }),
            builder.addCase(updateLanguage.fulfilled, (state, action) => {
                state.loading = false;
                state.globalLoader = false;
                state.globalConfig = {
                    ...state.globalConfig,
                    language: [action.payload]
                };
                state.currentLanguage = action.payload;
                state.error = {};
                state.showErrorModal = false;
            }),
            builder.addCase(updateLanguage.rejected, (state, action) => {
                state.loading = false;
                state.globalLoader = false;
                state.error = action.error;
                state.showErrorModal = true;
            }),
            builder.addCase(fetchNotifications.fulfilled, (state, action) => {
                state.notifications = action.payload;
            }),
            builder.addCase(fetchNotifications.rejected, () => {
                // Notifications are non-blocking; failures are logged in the thunk and must not
                // write to global.error, which other flows consume.
            });
    }
});

export const {
    updateErrorModalState,
    updateAdminConfig,
    updateGlobalLoaderState,
    updateLogo,
    updateSubdomain,
    resetGlobalReducer,
    handleOpenAgreementModal,
    updateShowDisclaimerFooter,
    updateCookiePolicyAlertBoxClosed,
    setOpenDrawer,
    updateFactsheetModalVisibility,
    updatePlanState,
    initializePlanState,
    setSelectedFundingTermIndex
} = globalSlice.actions;

export default globalSlice.reducer;
